/**
 * RecordPage — standard chrome for create / edit / view / settings / report screens.
 * Same ActionBar as the lists, pinned to the top of the scroll area:
 *
 *  ┌ Python Fundamentals [PUBLISHED]                               [right slot] [▽ Filters] ┐
 *  │ [←][↻]  [🔍 Find field…]  [💾 Save] [Save & close]  │ [Structure] [Preview] [Delete]   │
 *  └────────────────────────────────────────────────────────────────────────────────────────┘
 *    …form panels (children)…
 *
 * Props
 *  title, subtitle, badge        heading area
 *  onBack                        back tool (default: history back); pass null to hide
 *  onRefresh / refreshing        refresh tool
 *  primary / secondary           ActionBar action arrays (Save, Save & close | Delete, Structure …)
 *  findable                      show "Find field" search (default true). Matches form labels,
 *                                section titles and data-find attributes inside the page body.
 *  rightSlot, advancedSearch, activeFilterCount, trailing   passed through to ActionBar
 *  loading                       render a loading state instead of children
 *  maxWidth                      body max width class (default 'none' = full width; e.g. 'max-w-5xl' to centre a narrow form)
 *  dirty                         shows an "Unsaved changes" hint in the trailing slot
 *  side                          optional resizable side pane (split window), opened from an action:
 *                                { open, onOpenChange, title, icon, children, defaultSize (%, default 36),
 *                                  minSize (default 24), storageKey (persist width) }
 *                                The main body and the pane sit in a horizontal PanelGroup with a drag handle.
 *  tabs                          optional subtabs for a long page:
 *                                [{ key, label, icon, badge, content }]  — use INSTEAD of children.
 *                                `storageKey` on the page remembers the open tab between visits.
 *                                Every tab's content stays mounted (hidden with CSS), so unsaved
 *                                edits survive a tab switch and "Find field" can still see a field
 *                                on another tab — and switches to that tab when it finds one.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ActionBar from './ActionBar';
import SplitLayout from './SidePane';
import { ArrowLeft, ArrowsClockwise } from '@phosphor-icons/react';

const HIGHLIGHT = 'record-find-hit';

export default function RecordPage({
  title,
  subtitle,
  badge,
  onBack,
  onRefresh,
  refreshing = false,
  primary = [],
  secondary = [],
  findable = true,
  rightSlot,
  advancedSearch,
  activeFilterCount,
  trailing,
  loading = false,
  dirty = false,
  maxWidth = 'none',
  side = null,
  tabs = null,
  storageKey = null,
  children,
  className = '',
  'data-testid': testId = 'record-page',
}) {
  const navigate = useNavigate();
  const bodyRef = useRef(null);
  const [find, setFind] = useState('');
  const [hits, setHits] = useState(0);

  const tabList = Array.isArray(tabs) ? tabs.filter(Boolean) : null;
  const [activeTab, setActiveTab] = useState(() => {
    if (!tabList?.length) return null;
    try {
      const saved = storageKey && localStorage.getItem(`recordtab_${storageKey}`);
      if (saved && tabList.some((t) => t.key === saved)) return saved;
    } catch { /* private mode */ }
    return tabList[0].key;
  });
  // A page usually renders once as `loading` with no tabs at all, then again with them.
  // That first render is what sets the state above, so the remembered key can be stale
  // or null by the time the real tabs arrive — fall back to the first tab rather than
  // showing a row of tabs with nothing underneath.
  const currentTab = tabList?.some((t) => t.key === activeTab) ? activeTab : tabList?.[0]?.key ?? null;

  const openTab = (key) => {
    setActiveTab(key);
    try { if (storageKey) localStorage.setItem(`recordtab_${storageKey}`, key); } catch { /* ignore */ }
  };

  // Find field: highlight labels / headings that match, scroll to the first
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    root.querySelectorAll(`.${HIGHLIGHT}`).forEach((el) => el.classList.remove(HIGHLIGHT));
    const q = find.trim().toLowerCase();
    if (!q) { setHits(0); return; }
    const candidates = root.querySelectorAll('label, h2, h3, h4, [data-find], legend, th');
    let first = null;
    let count = 0;
    candidates.forEach((el) => {
      const text = (el.getAttribute('data-find') || el.textContent || '').toLowerCase();
      if (text.includes(q)) {
        el.classList.add(HIGHLIGHT);
        count += 1;
        if (!first) first = el;
      }
    });
    setHits(count);
    if (!first) return;
    // The field you searched for may be on a tab you are not looking at. Open it —
    // "0 matches" would be a lie, and a highlight nobody can see is worse.
    const owner = first.closest('[data-tab-panel]')?.getAttribute('data-tab-panel');
    if (owner && owner !== currentTab) {
      openTab(owner);
      window.setTimeout(() => first.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60);
      return;
    }
    first.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [find, children, tabs]);

  const tools = [];
  if (onBack !== null) {
    tools.push({ key: 'back', icon: ArrowLeft, title: 'Back', onClick: onBack || (() => navigate(-1)), testid: 'back-btn' });
  }
  if (onRefresh) {
    tools.push({ key: 'refresh', icon: ArrowsClockwise, title: 'Refresh', onClick: onRefresh, disabled: refreshing, spin: refreshing });
  }

  const trail = (
    <span className="flex items-center gap-3">
      {find && <span>{hits} match{hits === 1 ? '' : 'es'}</span>}
      {dirty && <span className="text-amber-700 font-medium" data-testid="record-dirty">Unsaved changes</span>}
      {trailing}
    </span>
  );

  return (
    <div className={`flex flex-col min-h-full ${className}`} data-testid={testId}>
      <div className="px-4 lg:px-5 pt-4 sticky top-0 z-20 bg-shell-canvas">
        <ActionBar
          title={title}
          subtitle={subtitle}
          badge={badge}
          tools={tools}
          search={findable ? { value: find, onChange: setFind, placeholder: 'Find field', width: 200, testid: 'record-find' } : null}
          primary={primary}
          secondary={secondary}
          rightSlot={rightSlot}
          advancedSearch={advancedSearch}
          activeFilterCount={activeFilterCount}
          trailing={trail}
        />
        {tabList?.length > 0 && !loading && (
          <div className="flex items-end gap-1 overflow-x-auto px-1 pt-1 -mb-px" role="tablist" data-testid="record-tabs">
            {tabList.map((t) => {
              const TabIcon = t.icon;
              const isActive = t.key === currentTab;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => openTab(t.key)}
                  className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-t-md border border-b-0 text-[13px] whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-white border-border text-shell-primary font-semibold shadow-[inset_0_2px_0_0_hsl(var(--shell-active))]'
                      : 'bg-muted/40 border-transparent text-foreground/70 hover:bg-muted hover:text-foreground'
                  }`}
                  data-testid={`record-tab-${t.key}`}
                >
                  {TabIcon && <TabIcon className="w-4 h-4" weight={isActive ? 'fill' : 'regular'} />}
                  {t.label}
                  {t.badge != null && (
                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-shell-primary/10 text-shell-primary text-[10px] font-semibold">
                      {t.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <SplitLayout side={side}>
        <div ref={bodyRef} className={`px-4 lg:px-5 py-3 space-y-3 w-full ${maxWidth === 'none' || side?.open ? '' : `${maxWidth} mx-auto`}`} data-testid="record-body">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground animate-pulse">Loading…</div>
          ) : tabList?.length ? (
            /* Every panel stays mounted: a half-typed field must survive a tab switch,
               and "Find field" has to be able to see a label on a tab you are not on. */
            tabList.map((t) => (
              <div
                key={t.key}
                data-tab-panel={t.key}
                className={`space-y-3 ${t.key === currentTab ? '' : 'hidden'}`}
              >
                {t.content}
              </div>
            ))
          ) : children}
        </div>
      </SplitLayout>
    </div>
  );
}

/** Section panel for forms: consistent card with a header line. */
export function FormSection({ title, icon: Icon, description, actions, children, className = '', bodyClassName = 'p-3' }) {
  return (
    <section className={`bg-white border border-border rounded-lg shadow-sm ${className}`}>
      {(title || actions) && (
        <header className="flex items-center gap-2 px-3 h-9 border-b border-border bg-muted/30">
          {Icon && <Icon className="w-4 h-4 text-shell-primary" weight="duotone" />}
          <h3 className="text-[13px] font-semibold mr-auto">{title}</h3>
          {actions}
        </header>
      )}
      {description && <p className="px-3 pt-2 text-[11px] text-muted-foreground">{description}</p>}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}
